import React from 'react';
import { useField } from 'formik';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface FormTextFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
    name: string;
    label?: string;
    helpText?: string;
    minLength?: number;
    maxLength?: number;
    regex?: string | RegExp;
    validate?: (value: unknown) => string | undefined | Promise<string | undefined>;
}

export const FormTextField: React.FC<FormTextFieldProps> = ({
    label,
    helpText,
    className,
    minLength,
    maxLength,
    regex,
    validate,
    ...props
}) => {
    const validateFn = (value: unknown) => {
        if (props.required && !value) return 'Campo obrigatório';
        if (minLength && value && String(value).length < minLength)
            return `Mínimo de ${minLength} caracteres`;
        if (maxLength && value && String(value).length > maxLength)
            return `Máximo de ${maxLength} caracteres`;
        if (regex && value) {
            const pattern = typeof regex === 'string' ? new RegExp(regex) : regex;
            if (!pattern.test(String(value))) return 'Formato inválido';
        }
        if (validate) return validate(value);
    };

    const [field, meta] = useField({ ...props, validate: validateFn });

    return (
        <div className={cn('space-y-2', className)}>
            {label && (
                <div className="space-y-1">
                    <Label
                        htmlFor={props.id || props.name}
                        className={cn(meta.touched && meta.error && 'text-destructive')}
                    >
                        {label} {props.required ? '*' : ''}
                    </Label>
                    {helpText ? (
                        <p className="text-sm leading-relaxed text-muted-foreground">{helpText}</p>
                    ) : null}
                </div>
            )}
            <Input
                {...field}
                {...props}
                id={props.id || props.name}
                className={cn(
                    meta.touched &&
                        meta.error &&
                        'border-destructive focus-visible:ring-destructive',
                )}
            />
            {meta.touched && meta.error ? (
                <p className="text-[0.8rem] font-medium text-destructive">{meta.error}</p>
            ) : null}
        </div>
    );
};
